import { useCallback, useEffect, useRef, useState, type ReactNode } from 'react';
import { Alert, Box, Button, Stack, Typography } from '@mui/material';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ErrorCodes, isApiError, isNotFound } from '@/api/errors';
import { errorCopy } from '@/api/errorCopy';
import { referralApi, type ReferralLookup } from '@/api/referral';
import { ErrorState } from '@/components/ErrorState';
import { formatInstant } from '@/i18n/format';
import { useLocale } from '@/i18n/useLocale';
import { useMe } from '@/session/meHooks';
import { PageHeader } from '@/shell/PageHeader';
import { StandaloneFrame } from '@/shell/StandaloneFrame';
import { usePageTitle } from '@/shell/usePageTitle';
import { clearReferral } from './referralStore';

type PageStatus = 'loading' | 'ready' | 'notFound' | 'error';

export function ReferralPage() {
  const { t } = useTranslation();
  const locale = useLocale();
  const timeZone = useMe().user.timezone ?? 'UTC';
  const navigate = useNavigate();
  const { code } = useParams();
  const [status, setStatus] = useState<PageStatus>('loading');
  const [lookup, setLookup] = useState<ReferralLookup | null>(null);
  const [busy, setBusy] = useState(false);
  // The lookup's failure is the page's (an `ErrorState`); the register / switch writes fail at the
  // action they belong to (TMM-B3), and never replace the page.
  const [loadError, setLoadError] = useState<unknown>(undefined);
  const [writeError, setWriteError] = useState<string | null>(null);
  const [registerTick, setRegisterTick] = useState(0);
  const started = useRef(false);
  const registered = useRef(false);
  const title = usePageTitle({ title: t('referral.title') });

  const load = useCallback(async () => {
    if (!code) {
      setStatus('notFound');
      clearReferral();
      return;
    }
    setStatus('loading');
    setWriteError(null);
    try {
      const result = await referralApi.lookup(code);
      setLookup(result);
      setStatus('ready');
    } catch (error) {
      clearReferral();
      if (
        isApiError(error) &&
        (isNotFound(error) || error.code === ErrorCodes.REFERRAL_NOT_FOUND)
      ) {
        setStatus('notFound');
        return;
      }
      setLoadError(error);
      setStatus('error');
    }
  }, [code]);

  useEffect(() => {
    if (started.current) return;
    started.current = true;
    void load();
  }, [load]);

  useEffect(() => {
    if (status !== 'ready' || !lookup || !code) return;
    if (lookup.state === 'CAN_REGISTER' && !registered.current) {
      registered.current = true;
      void (async () => {
        try {
          const result = await referralApi.register({ code });
          setLookup(result);
        } catch (error) {
          setWriteError(errorCopy(t, error, 'referral.registerError'));
        }
        clearReferral();
      })();
      return;
    }
    if (lookup.state !== 'CAN_REGISTER') {
      clearReferral();
    }
    // `registerTick` re-runs the automatic registration after a failed attempt.
  }, [code, lookup, status, registerTick, t]);

  function retryRegister() {
    registered.current = false;
    setWriteError(null);
    setRegisterTick((n) => n + 1);
  }

  async function handleSwitch() {
    if (!code || busy) return;
    setBusy(true);
    setWriteError(null);
    try {
      const result = await referralApi.register({ code, confirm: true });
      setLookup(result);
    } catch (error) {
      setWriteError(errorCopy(t, error, 'referral.switchError'));
    } finally {
      clearReferral();
      setBusy(false);
    }
  }

  function handleContinue() {
    void navigate('/');
  }

  // One `PageHeader` for every state, so the `h1` stays put while the lookup resolves. The page
  // is outside the shell, so it brings its own `<main>` and skip link (A11Y).
  const shell = (body: ReactNode) => (
    <StandaloneFrame>
      <PageHeader title={title} />
      {body}
    </StandaloneFrame>
  );

  if (status === 'loading') {
    return shell(<Typography variant="body2">{t('common.loading')}</Typography>);
  }
  if (status === 'notFound') {
    return shell(
      <Stack spacing={2} sx={{ maxWidth: 560 }}>
        <Typography variant="body1">{t('referral.notFound')}</Typography>
        <Box>
          <Button variant="contained" onClick={handleContinue}>
            {t('referral.continue')}
          </Button>
        </Box>
      </Stack>,
    );
  }
  if (status === 'error' || lookup === null) {
    return shell(
      <ErrorState
        error={loadError}
        what={t('errors.what.referral')}
        onRetry={() => {
          started.current = false;
          registered.current = false;
          void load();
        }}
      />,
    );
  }

  const affiliate = lookup.affiliateDisplayName;
  const current = lookup.current?.affiliateDisplayName ?? null;
  // The switch window's end, when served (TMMAPP-0136 item 13): CAN_REGISTER and CAN_SWITCH only.
  const until =
    lookup.changeableUntil !== null &&
    (lookup.state === 'CAN_REGISTER' || lookup.state === 'CAN_SWITCH')
      ? formatInstant(lookup.changeableUntil, locale, timeZone, { dateStyle: 'long' })
      : null;

  return shell(
    <Stack spacing={2} sx={{ maxWidth: 560 }}>
      {lookup.state === 'CAN_REGISTER' && writeError === null && (
        <Typography variant="body1">{t('referral.registered', { affiliate })}</Typography>
      )}
      {lookup.state === 'CAN_REGISTER' && writeError !== null && (
        <Alert
          severity="error"
          action={
            <Button color="inherit" size="small" onClick={retryRegister}>
              {t('common.retry')}
            </Button>
          }
        >
          {writeError}
        </Alert>
      )}
      {until !== null && (
        <Typography variant="body2" data-referral-until="" sx={{ color: 'text.secondary' }}>
          {t('referral.changeableBy', { date: until })}
        </Typography>
      )}
      {lookup.state === 'ALREADY_THIS' && (
        <Typography variant="body1">{t('referral.alreadyThis', { affiliate })}</Typography>
      )}
      {lookup.state === 'CAN_SWITCH' && (
        <>
          <Typography variant="body1">
            {t('referral.switchAsk', { current: current ?? '', affiliate })}
          </Typography>
          {writeError !== null && <Alert severity="error">{writeError}</Alert>}
          <Stack direction="row" spacing={1}>
            <Button variant="contained" disabled={busy} onClick={() => void handleSwitch()}>
              {t('referral.switch')}
            </Button>
            <Button onClick={handleContinue}>{t('referral.keep')}</Button>
          </Stack>
        </>
      )}
      {lookup.state === 'LOCKED' && (
        <Typography variant="body1">
          {current ? t('referral.locked', { current }) : t('referral.lockedNone')}
        </Typography>
      )}
      {(lookup.state === 'NO_SUBSCRIPTION' || lookup.state === null) && (
        <Typography variant="body1">{t('referral.lockedNone')}</Typography>
      )}
      {lookup.state !== 'CAN_SWITCH' && (
        <Box>
          <Button variant="contained" onClick={handleContinue}>
            {t('referral.continue')}
          </Button>
        </Box>
      )}
    </Stack>,
  );
}
